import qs from 'query-string';
import React, { Fragment, useCallback, useState } from 'react';
import { useExpanded, useFilters, usePagination, useTable } from 'react-table';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Col, Row, Table } from 'react-bootstrap';
import { BsExclamationCircle } from 'react-icons/bs';
import { DefaultColumnFilter, filterTypes } from './TableFilter';
import './Table.scss';
import classnames from 'classnames';
import { useMediaQuery } from 'react-responsive/src';
import Pagination from './Pagination';
import { TABLE_PAGE_SIZE } from '../utils/constants';

const DarsupTable = props => {
  let {
    data,
    columns,
    hasPagination = false,
    hasFilter = false,
    hasSearch = false,
    hasCounterColumn = false,
    tablePageSize = 10,
    count,
    toggledRow,
    toggledRowProps = null,
    noDataText,
    showToggledRow = true,
    currentPage,
    onPageChange,
    pageParamName = 'page',
    loading,
  } = props;

  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');

  const params = new URLSearchParams(useLocation().search);

  const pageNumberQuery = Number(params.get('page'));

  const history = useHistory();

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    []
  );
  const tablePageCount = Math.ceil(count / tablePageSize);
  let isMDScreen = useMediaQuery({ query: '(min-width: 768px)' });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    gotoPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
      initialState: {
        pageIndex: pageNumberQuery ? pageNumberQuery - 1 : 0,
        hiddenColumns: ['id'],
      },
      manualPagination: true,
      autoResetPage: false,
      autoResetSortBy: false,
      pageCount: tablePageCount,
    },
    useExpanded,
    hasFilter && useFilters,
    hasPagination && usePagination
  );

  const handleSearch = e => {
    e.preventDefault();
    if (hasSearch) {
      hasPagination && gotoPage(0);
      const query = {
        ...(hasSearch && searchValue && { search: searchValue || '' }),
        ...(sortValue && { ordering: sortValue || '' }),
      };
      const searchString = qs.stringify(query);
      history.push({
        search: searchString,
      });
    }
  };

  const handleSort = column => {
    if (column.hasSort) {
      if (!sortValue) {
        setSortValue(column.id);
      } else if (sortValue === column.id) {
        setSortValue(`-${column.id}`);
      } else if (sortValue[0] === '-') {
        setSortValue('');
      }
    }
  };

  const renderRowSubComponent = useCallback((row, toggledRowProps) => toggledRow(row, toggledRowProps), [toggledRow]);

  return (
    <Fragment>
      {hasSearch && (
        <>
          <div className="mb-2 table-search">
            <form onSubmit={handleSearch}>
              <input
                value={searchValue || ''}
                onChange={e => {
                  setSearchValue(e.target.value);
                }}
                placeholder={`جستجو...`}
              />
              <i className="fas fa-search" onClick={handleSearch} />
            </form>
          </div>
        </>
      )}

      <div className={'table-root'}>
        <Table {...getTableProps()} bordered striped hover variant="light">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {hasCounterColumn && isMDScreen && (
                  <th className="font-weight-bold" style={{ width: '5%' }}>
                    {'ردیف'}
                  </th>
                )}
                {headerGroup.headers.map(column => {
                  return (
                    <th className="font-weight-bold" {...column.getHeaderProps()}>
                      <span
                        className="d-flex align-items-center"
                        onClick={() => handleSort(column)}
                        style={column.hasSort ? { cursor: 'pointer' } : { cursor: 'default' }}
                      >
                        <span
                          style={{
                            textAlign: 'center',
                            width: '100%',
                            color: '#334f55',
                            fontSize: '15px',
                          }}
                        >
                          {column.render('Header')}
                        </span>
                        {column.hasSort && (
                          <>
                            {sortValue ? (
                              sortValue[0] === '-' ? (
                                <i style={{ fontSize: '20px' }} className="fas fa-sort-down mr-2" />
                              ) : (
                                <i style={{ fontSize: '20px' }} className="fas fa-sort-up mr-2" />
                              )
                            ) : (
                              ''
                            )}
                          </>
                        )}
                      </span>
                      <div>{column.canFilter ? column.render('Filter') : null}</div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          {!!data?.length && (
            <tbody {...getTableBodyProps()}>
              {hasPagination
                ? page.map(row => {
                    prepareRow(row);
                    return (
                      <>
                        <tr style={{ backgroundColor: '#ffffff00' }} {...row.getRowProps()}>
                          {hasCounterColumn && isMDScreen && (
                            <td style={{ textAlign: 'center' }}>
                              {Number(row.id) + tablePageSize * (pageNumberQuery - 1) + 1}
                            </td>
                          )}

                          {row.cells.map(cell => {
                            return (
                              <Fragment>
                                <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                              </Fragment>
                            );
                          })}
                        </tr>

                        {row.isExpanded && showToggledRow ? (
                          <tr>
                            <td className={'expandedSectionContainer'} colSpan={6}>
                              {renderRowSubComponent(row, toggledRowProps)}
                            </td>
                          </tr>
                        ) : null}
                      </>
                    );
                  })
                : rows.map(row => {
                    if (toggledRowProps) row.additionalProps = toggledRowProps;
                    prepareRow(row);
                    return (
                      <>
                        <tr style={{ backgroundColor: '#ffffff00' }} {...row.getRowProps()}>
                          {hasCounterColumn && isMDScreen && (
                            <td style={{ textAlign: 'center' }}>{Number(row.id) + 1}</td>
                          )}
                          {row.cells.map(cell => {
                            return (
                              <Fragment>
                                <td
                                  {...cell.getCellProps()}
                                  className={classnames({ 'time-fixed-width': cell?.column?.colName === 'time' })}
                                >
                                  {cell.render('Cell')}
                                </td>
                              </Fragment>
                            );
                          })}
                        </tr>

                        {row.isExpanded && showToggledRow ? (
                          <tr>
                            <td className={'expandedSectionContainer'} colSpan={6}>
                              {renderRowSubComponent(row, toggledRowProps)}
                            </td>
                          </tr>
                        ) : null}
                      </>
                    );
                  })}
            </tbody>
          )}
        </Table>
        {!data?.length && (
          <div className={'not-found-table-wrapper my-5'}>
            <BsExclamationCircle size={25} />
            <p>{noDataText ? noDataText : 'نتیجه ای یافت نشد'}</p>
          </div>
        )}
      </div>
      {currentPage && data.length ? (
        <Row>
          <Col>
            <div className={'my-5'}>
              <Col xs={12}>
                <Pagination
                  currentPage={currentPage}
                  totalItems={count}
                  onPageChange={page => onPageChange(page, pageParamName)}
                  pageSize={TABLE_PAGE_SIZE}
                  disabled={loading}
                />
              </Col>
            </div>
          </Col>
        </Row>
      ) : null}
    </Fragment>
  );
};

DarsupTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  hasPagination: PropTypes.bool,
  hasFilter: PropTypes.bool,
  hasCounterColumn: PropTypes.bool,
  hasSearch: PropTypes.bool,
  sortFields: PropTypes.array,
  fetchData: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
  toggledRow: PropTypes.node,
  noDataText: PropTypes.string,
  showToggledRow: PropTypes.bool,
  toggledRowProps: PropTypes.object,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  pageParamName: PropTypes.string,
  loading: PropTypes.bool,
};

export default DarsupTable;
